import React, {useEffect} from "react";
import {Switch} from "react-router-dom";
import {ToastContainer, Slide} from "react-toastify";
import {useAuth0} from "@auth0/auth0-react";

import {NavBar, Footer, EnvBadge} from "./components";
import Loader from "./components/loader";
import AuthError from "./auth/auth-error";
import {
  Dashboard,
  MetricsOpportunities,
  EditFlight,
  AirportsAPI,
  Airports,
  SingleAirport,
  Aircrafts,
  SingleAircraft,
  AircraftTypes,
  SingleAircraftType,
  DistanceParams,
  SingleDistanceParam,
  Spreads,
  Calculator,
  Leads,
  SingleLead,
  DuplicateQuote,
  NewQuote,
  SingleQuote,
  Quotes,
  QFS,
  PhoneCalls,
  PhoneCallQuote,
  Estimates,
  MyLeads,
  Organizations,
  SingleOrganization,
  NewOrganization,
  NewUser,
  Users,
  SingleUser,
  MyAccount,
  TripRequests,
  InProgressLeads,
  ArchivedLeads,
  QFSLeads,
  UnqualifiedLeads,
  WarmProspectLeads,
  Export,
  MyTeamLeads,
  SingleTripRequest,
  OutsideOrgSingleQuote,
  OutsideOrgUsers,
  OutsideOrgNewUser,
  OutsideOrgSingleUser,
  FlightBoard,
  NewAirport,
  OPI,
  SingleOperator,
  UpdateOPIWeights,
  NewOperator,
  NewExclusionRule,
  NewCountrySetting,
  SingleCountrySetting,
  CountrySettings,
  ExclusionRules,
  SingleExclusionRule,
  FlightLegs,
  SingleFlightLeg,
  ArchivedMatches,
  ClosedRepeatLeads,
  ClosedGhostLeads,
  ClosedLostToCompetitorLeads,
  InLimboLeads,
  SignedContractLeads,
  TripCompleteLeads,
  Prospects,
  CreateProspect,
  SingleProspect,
  NewLeads,
  Developers,
  RestrictedOperators,
  NewContract,
  SingleSpread,
  SourcingModule,
  SourcingList,
  LeadFromScratch,
  OFRBuilder,
  SingleTrip,
  ContractPreview,
  NewItinerary,
  ItineraryPreview,
  Trips,
  TripVerification,
  SignedContracts,
  ClickToFly,
  SingleClickToFly,
  NewClickToFly,
  ClickToFlyPricingConfig,
  FeaturedOneWayConfig,
  ClickToFlyCalculator,
  ClickToFlyHub,
  ClickToFlyResults,
  SingleClickToFlyResult,
  Accounting,
  PeakDays,
  Commissions,
  SeasonalPricing,
  FeaturedOneWay,
  PaymentsLinking,
  XeroCallback,
  MetricsUtilization,
  CateringConfiguration,
  CateringItems,
  MetricsProspects,
  MetricsLocations,
  MetricsSales,
  PendingPayments,
  DutyStatusManagement,
  LeadAcceptanceConfig,
  FundsLedger,
} from "./views";
import AdminRoute from "./auth/admin-route";
import InternalUserRoute from "./auth/internal-user-route";
import AccountingLedgerRoute from "./auth/accounting-ledger-route";
import AccountingPendingRoute from "./auth/accounting-pending-route";
import AdminOrDutyAdminRoute from "./auth/admin-or-duty-admin-route";
import AdminOrSalesRoute from "./auth/admin-or-sales-route";
import AdminOrADRoute from "./auth/admin-or-ad-route";
import AdminSalesOutsideOrgRoute from "./auth/admin-sales-outside-org.js";
import OutsideOrgRoute from "./auth/outside-org-route";
import OutsideOrgAdminRoute from "./auth/outside-org-admin-route";
import AdminWidgets from "./components/admin-widgets/adminWidgets";
import {setOnUnauthorized} from "./helpers/axios";
import NewInvoice from "./views/new-invoice";
import InvoicePreview from "./views/invoice-preview.js";
import OutsideOrganizationHome from "./components/outside-organizations/outside-organization-home";

import "./app.css";

const HomeRouter = () => {
  const {user} = useAuth0();
  const outsideOrg =
    user && user["https://new.evojets.com/outsideOrganization"];
  return outsideOrg ? <OutsideOrganizationHome/> : <Dashboard/>;
};

const App = () => {
  const {
    isLoading,
    isAuthenticated,
    error,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();

  useEffect(() => {
    setOnUnauthorized(() =>
      loginWithRedirect({
        appState: {returnTo: window.location.pathname},
      }),
    );
  }, [loginWithRedirect]);

  // Runs once per auth-state change (not on every render), and never during
  // render: a silent-auth failure or an unauthenticated visit triggers exactly
  // one interactive login. If that login comes back with an error, stop and
  // show it instead of redirecting again.
  useEffect(() => {
    if (isLoading || error) return;
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } });
      return;
    }
    getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
    });
  }, [
    isLoading,
    isAuthenticated,
    error,
    loginWithRedirect,
    getAccessTokenSilently,
  ]);

  if (error && !isLoading) {
    return <AuthError error={error}/>;
  }

  if (!isAuthenticated && !isLoading) {
    return null;
  }

  if (isLoading) {
    return <Loader/>;
  }

  const windows = navigator.platform.includes("Win");

  return (
    <div
      id="app"
      className={`d-flex flex-column h-100  ${windows && "os-windows"}`}
    >
      {isAuthenticated && <NavBar/>}
      <EnvBadge/>
      <ToastContainer icon={false} transition={Slide}/>

      <main id="page-wrap">
        <div className="container flex-grow-1">
          <Switch>
            {/* trips start */}

            <AdminOrSalesRoute
              path="/contracts/new/:leadId?/:quoteId?/:aircraftOptionId?/:action?"
              exact
              component={NewContract}
            />
            <AdminOrSalesRoute
              path="/trips/:tripId/contracts/:contractId"
              exact
              component={NewContract}
            />
            <AdminOrSalesRoute
              path="/:tripId/contracts/new"
              exact
              component={NewContract}
            />

            <AdminOrSalesRoute
              path="/contracts/:contractId/preview"
              exact
              component={ContractPreview}
            />
            <AdminOrSalesRoute
              path="/trips/:tripId/contracts/new/:quoteId?/:aircraftOptionId?/:leadId?"
              exact
              component={NewContract}
            />

            <AdminOrSalesRoute
              path="/trips/:tripId/invoices/new"
              exact
              component={NewInvoice}
            />
            <AdminOrSalesRoute
              path="/trips/:tripId/invoices/:invoiceId"
              exact
              component={NewInvoice}
            />
            <AdminOrSalesRoute
              path="/trips/:tripId/invoices/:invoiceId/preview"
              exact
              component={InvoicePreview}
            />

            <AdminOrSalesRoute path="/trips" exact component={Trips}/>
            <AdminOrSalesRoute
              path="/trips/verification"
              exact
              component={TripVerification}
            />
            <AdminOrSalesRoute
              path="/signed-contracts"
              exact
              component={SignedContracts}
            />

            <AdminOrSalesRoute
              path="/trips/:tripId/contracts/:contractId/preview"
              exact
              component={ContractPreview}
            />
            <AdminOrSalesRoute
              path="/trips/:tripId/contracts/:contractId/:action"
              exact
              component={NewContract}
            />
            <AdminOrSalesRoute
              path="/trips/:tripId/itineraries/new"
              exact
              component={NewItinerary}
            />
            <AdminOrSalesRoute
              path="/trips/:tripId/itineraries/:itineraryId"
              exact
              component={NewItinerary}
            />

            <AdminOrSalesRoute
              path="/trips/:tripId/itineraries/:itineraryId/preview"
              exact
              component={ItineraryPreview}
            />

            <AdminOrSalesRoute path="/trips/:id" exact component={SingleTrip}/>

            <AdminOrSalesRoute
              path="/trips/:tripId/financials/new"
              exact
              component={OFRBuilder}
            />
            <AdminOrSalesRoute
              path="/trips/:tripId/financials/:id"
              exact
              component={OFRBuilder}
            />

            {/* trips end */}

            <AdminSalesOutsideOrgRoute Route path="/" exact component={HomeRouter}/>
            <AdminRoute Route path="/metrics" exact component={MetricsOpportunities}/>
            <AdminRoute Route path="/metrics/utilization" exact component={MetricsUtilization}/>
            <AdminRoute Route path="/metrics/locations" exact component={MetricsLocations}/>
            <AdminRoute Route path="/metrics/prospects" exact component={MetricsProspects}/>
            <AdminRoute Route path="/metrics/sales" exact component={MetricsSales}/>
            <AdminSalesOutsideOrgRoute
              path="/my-account"
              component={MyAccount}
            />
            <AdminOrSalesRoute path="/atc/:id" component={EditFlight}/>

            <AdminRoute path="/export" exact component={Export}/>

            <AdminOrSalesRoute path="/leads" exact component={Leads}/>
            <AdminOrSalesRoute path="/my-leads" exact component={MyLeads}/>
            <AdminOrSalesRoute
              path="/my-team-leads"
              exact
              component={MyTeamLeads}
            />

            <AdminOrSalesRoute
              path="/flight-matches"
              exact
              component={FlightLegs}
            />

            <AdminOrSalesRoute
              path="/archived-matches"
              exact
              component={ArchivedMatches}
            />

            <AdminOrSalesRoute
              path="/flight-matches/:id"
              exact
              component={SingleFlightLeg}
            />

            <AdminOrSalesRoute path="/opi" exact component={OPI}/>
            <AdminOrSalesRoute
              path="/restricted-operators"
              exact
              component={RestrictedOperators}
            />

            <AdminOrADRoute path="/opi/new" exact component={NewOperator}/>

            <AdminOrADRoute path="/opi/:id" exact component={SingleOperator}/>

            <AdminRoute
              path="/exclusion-rules/new"
              exact
              component={NewExclusionRule}
            />

            <AdminOrSalesRoute path="/prospects" exact component={Prospects}/>

            <AdminOrSalesRoute
              path="/prospects/create"
              exact
              component={CreateProspect}
            />
            <AdminOrSalesRoute
              path="/prospects/:id"
              exact
              component={SingleProspect}
            />

            <AdminRoute
              path="/exclusion-rules/:id"
              exact
              component={SingleExclusionRule}
            />

            <AdminRoute
              path="/country-settings/new"
              exact
              component={NewCountrySetting}
            />

            <AdminRoute
              path="/country-settings/:id"
              exact
              component={SingleCountrySetting}
            />

            <AdminRoute
              path="/country-settings/"
              exact
              component={CountrySettings}
            />

            <AdminRoute
              path="/exclusion-rules/"
              exact
              component={ExclusionRules}
            />

            <AdminRoute path="/widgets" exact component={AdminWidgets}/>

            <AdminRoute
              path="/opi-weights"
              exact
              component={UpdateOPIWeights}
            />

            <AdminOrSalesRoute
              path="/flight-board"
              exact
              component={FlightBoard}
            />

            <AdminOrSalesRoute
              path="/leads/archived"
              exact
              component={ArchivedLeads}
            />
            <AdminOrSalesRoute path="/leads/new" exact component={NewLeads}/>

            <AdminOrSalesRoute
              path="/leads/in-progress"
              exact
              component={InProgressLeads}
            />
            <AdminOrSalesRoute
              path="/leads/unqualified"
              exact
              component={UnqualifiedLeads}
            />
            <AdminOrSalesRoute
              path="/leads/warm-prospects"
              exact
              component={WarmProspectLeads}
            />
            <AdminOrSalesRoute
              path="/leads/closed-repeat"
              exact
              component={ClosedRepeatLeads}
            />

            <AdminOrSalesRoute
              path="/leads/closed-ghost"
              exact
              component={ClosedGhostLeads}
            />

            <AdminOrSalesRoute
              path="/leads/closed-lost"
              exact
              component={ClosedLostToCompetitorLeads}
            />

            <AdminOrSalesRoute
              path="/leads/in-limbo"
              exact
              component={InLimboLeads}
            />

            <AdminOrSalesRoute
              path="/leads/signed-contract"
              exact
              component={SignedContractLeads}
            />

            <AdminOrSalesRoute
              path="/leads/trip-complete"
              exact
              component={TripCompleteLeads}
            />

            <AdminOrSalesRoute path="/leads/qfs" exact component={QFSLeads}/>

            <OutsideOrgRoute
              path="/trip-requests"
              exact
              component={TripRequests}
            />

            <OutsideOrgRoute
              path="/developers"
              exact
              component={Developers}
            />

            <OutsideOrgRoute
              path="/trip-requests/:id"
              exact
              component={SingleTripRequest}
            />
            <AdminOrSalesRoute
              path="/leads/:id"
              exact
              component={SingleLead}
            />
            <AdminOrSalesRoute
              path="/leads/:id/quotes/new"
              exact
              component={NewQuote}
            />

            <AdminRoute path="/payments-linking" exact component={PaymentsLinking}/>
            <AdminRoute path="/xero-callback" exact component={XeroCallback}/>
            <InternalUserRoute path="/accounting" exact component={Accounting}/>
            <AccountingLedgerRoute path="/funds-ledger" exact component={FundsLedger}/>
            <AccountingPendingRoute path="/pending-payments" exact component={PendingPayments}/>
            <AdminRoute path="/users" exact component={Users}/>

            <OutsideOrgAdminRoute
              path="/settings/users"
              exact
              component={OutsideOrgUsers}
            />
            <OutsideOrgAdminRoute
              path="/settings/users/new"
              exact
              component={OutsideOrgNewUser}
            />
            <OutsideOrgAdminRoute
              path="/settings/users/:id"
              exact
              component={OutsideOrgSingleUser}
            />

            <AdminRoute path="/users/new" exact component={NewUser}/>
            <AdminRoute path="/users/:id" exact component={SingleUser}/>
            <AdminRoute path="/commissions" exact component={Commissions}/>
            <AdminRoute path="/estimates" exact component={Estimates}/>
            <AdminRoute
              path="/organizations"
              exact
              component={Organizations}
            />
            <AdminOrSalesRoute path="/quotes" exact component={Quotes}/>
            <AdminOrSalesRoute
              path="/quotes/:id"
              exact
              component={SingleQuote}
            />

            <OutsideOrgRoute
              path="/quote-preview/:id"
              exact
              component={OutsideOrgSingleQuote}
            />

            <AdminOrSalesRoute
              path="/quotes/:quoteId/edit"
              exact
              component={NewQuote}
            />

            <AdminOrSalesRoute
              path="/quotes/:quoteId/duplicate/:aircraftOptionId/:leadId?"
              exact
              component={DuplicateQuote}
            />

            <AdminOrSalesRoute path="/new-quote" exact component={QFS}/>
            <AdminOrSalesRoute
              path="/new-lead"
              exact
              component={LeadFromScratch}
            />

            <AdminOrSalesRoute
              path="/phone-calls"
              exact
              component={PhoneCalls}
            />
            <AdminOrSalesRoute
              path="/phone-calls/:id/new-quote"
              exact
              component={PhoneCallQuote}
            />

            <AdminOrSalesRoute
              path="/sourcing/new"
              exact
              component={SourcingModule}
            />

            <AdminOrSalesRoute
              path="/sourcing/:id"
              exact
              component={SourcingModule}
            />

            <AdminOrSalesRoute
              path="/sourcing"
              exact
              component={SourcingList}
            />

            <AdminRoute
              path="/organizations/new"
              exact
              component={NewOrganization}
            />
            <AdminRoute
              path="/organizations/:id"
              exact
              component={SingleOrganization}
            />
            <AdminRoute path="/airports-api" component={AirportsAPI}/>
            <AdminRoute path="/airports" exact component={Airports}/>
            <AdminRoute path="/airports/new" component={NewAirport}/>
            <AdminRoute path="/airports/:id" component={SingleAirport}/>
            <AdminRoute
              path="/click-to-fly"
              exact
              component={ClickToFlyHub}
            />
            <AdminRoute
              path="/click-to-fly/pricing-config"
              component={ClickToFlyPricingConfig}
            />
            <AdminRoute
              path="/featured-one-way-config"
              component={FeaturedOneWayConfig}
            />
            <AdminOrSalesRoute
              path="/click-to-fly/calculator"
              component={ClickToFlyCalculator}
            />
            <AdminRoute
              path="/click-to-fly/routes"
              exact
              component={ClickToFly}
            />
            <AdminRoute
              path="/click-to-fly/routes/new"
              component={NewClickToFly}
            />
            <AdminRoute
              path="/click-to-fly/routes/:id"
              component={SingleClickToFly}
            />
            <AdminOrSalesRoute
              path="/click-to-fly/results"
              exact
              component={ClickToFlyResults}
            />
            <AdminOrSalesRoute
              path="/click-to-fly/results/:id"
              component={SingleClickToFlyResult}
            />
            <AdminRoute path="/aircraft" exact component={Aircrafts}/>
            <AdminRoute path="/aircraft/:id" component={SingleAircraft}/>
            <AdminRoute
              path="/aircraft-types"
              exact
              component={AircraftTypes}
            />
            <AdminRoute
              path="/aircraft-types/:id"
              component={SingleAircraftType}
            />
            <AdminRoute
              path="/distance-params"
              exact
              component={DistanceParams}
            />
            <AdminRoute
              path="/distance-params/:id"
              exact
              component={SingleDistanceParam}
            />
            <AdminRoute path="/spreads" exact component={Spreads}/>

            <AdminRoute path="/spreads/:id" exact component={SingleSpread}/>

            <AdminSalesOutsideOrgRoute
              path="/calculator"
              exact
              component={Calculator}
            />
            <AdminRoute
              path="/peak-days"
              exact
              component={PeakDays}
            />
            <AdminRoute
              path="/seasonal-pricing"
              exact
              component={SeasonalPricing}
            />

            <AdminRoute
              path="/featured-one-ways"
              exact
              component={FeaturedOneWay}
            />

            <AdminRoute
              path="/catering-configuration"
              exact
              component={CateringConfiguration}
            />

            <AdminRoute
              path="/catering-items"
              exact
              component={CateringItems}
            />

            <AdminOrDutyAdminRoute path="/duty-status" exact component={DutyStatusManagement}/>
            <AdminRoute
              path="/lead-acceptance-config"
              component={LeadAcceptanceConfig}
            />
          </Switch>
        </div>
        <Footer/>
      </main>
    </div>
  );
};

export default App;
